import React, { useState, useEffect } from 'react';
import {
  Calendar, AlertCircle, Clock, CheckCircle, ChevronLeft,
  ChevronRight, DollarSign, CreditCard, Receipt, Filter,
  Bell, AlertTriangle, X, Check
} from 'lucide-react';

interface PaymentItem {
  payment_type: 'purchase' | 'expense';
  reference_id: number;
  reference_number: string;
  payee_name: string;
  payment_due_date: string;
  amount: number;
  payment_status: 'unpaid' | 'partial' | 'paid';
  payment_date?: string;
  payment_method?: string;
  invoice_number?: string;
  notes?: string;
}

interface Alert {
  payment_type: string;
  reference: string;
  payee: string;
  due_date: string;
  amount: number;
  days_overdue?: number;
  days_remaining?: number;
}

interface CalendarDay {
  date: string;
  items: PaymentItem[];
  total: number;
  unpaid_total: number;
}

const PaymentScheduleCalendar: React.FC = () => {
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [calendarData, setCalendarData] = useState<{ [key: string]: CalendarDay }>({});
  const [alerts, setAlerts] = useState<{
    overdue: Alert[];
    urgent: Alert[];
    upcoming: Alert[];
  }>({ overdue: [], urgent: [], upcoming: [] });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterType, setFilterType] = useState<'all' | 'purchase' | 'expense'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'unpaid' | 'paid'>('all');
  const [showAlerts, setShowAlerts] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear, filterType, filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // カレンダーデータ取得
      const calendarRes = await fetch(
        `/api/payment-schedule/calendar?year=${selectedYear}&month=${selectedMonth + 1}`
      );
      const calendarResult = await calendarRes.json();
      if (calendarResult.success) {
        setCalendarData(calendarResult.data);
      }

      // 支払予定一覧取得
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);

      const paymentsRes = await fetch(`/api/payment-schedule?${params}`);
      const paymentsResult = await paymentsRes.json();
      if (paymentsResult.success) {
        setPayments(paymentsResult.data);
      }

      // アラート取得
      const alertsRes = await fetch('/api/payment-schedule/alerts');
      const alertsResult = await alertsRes.json();
      if (alertsResult.success) {
        setAlerts(alertsResult.data);
      }
    } catch (err) {
      console.error('データ取得エラー:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentExecute = async (payment: PaymentItem) => {
    const paymentDate = prompt('支払日を入力してください (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!paymentDate) return;

    const paymentMethod = prompt('支払方法を選択してください (cash/transfer/credit):', 'transfer');
    if (!paymentMethod) return;

    const paymentAmount = prompt(`支払金額を入力してください (請求額: ¥${payment.amount.toLocaleString()}):`);
    if (!paymentAmount) return;

    try {
      const response = await fetch('/api/payment-schedule/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_type: payment.payment_type,
          reference_id: payment.reference_id,
          payment_date: paymentDate,
          payment_method: paymentMethod,
          payment_amount: parseFloat(paymentAmount)
        })
      });

      const result = await response.json();
      if (result.success) {
        alert('支払を記録しました');
        fetchData();
      } else {
        throw new Error(result.message);
      }
    } catch (err: any) {
      alert(err.message || '支払記録に失敗しました');
    }
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDate = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'partial':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'unpaid':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getPaymentTypeIcon = (type: string) => {
    return type === 'purchase' ? (
      <Receipt className="w-3 h-3 text-blue-500" />
    ) : (
      <CreditCard className="w-3 h-3 text-purple-500" />
    );
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDayOfMonth = getFirstDayOfMonth(selectedYear, selectedMonth);
    const days = [];

    // 前月の空白
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 border border-gray-100" />);
    }

    // 各日付
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDate(selectedYear, selectedMonth, day);
      const dayData = calendarData[dateStr];
      const isToday = dateStr === new Date().toISOString().split('T')[0];
      const isSelected = dateStr === selectedDate;

      days.push(
        <div
          key={day}
          className={`p-2 border cursor-pointer transition-colors ${
            isToday ? 'bg-blue-50 border-blue-300' : 'border-gray-200'
          } ${isSelected ? 'ring-2 ring-blue-500' : ''} hover:bg-gray-50`}
          onClick={() => setSelectedDate(dateStr)}
        >
          <div className="text-sm font-medium mb-1">{day}</div>
          {dayData && (
            <div>
              <div className="text-xs space-y-1">
                {dayData.items.slice(0, 2).map((item, index) => (
                  <div key={index} className="flex items-center gap-1">
                    {getPaymentTypeIcon(item.payment_type)}
                    <span className="truncate">{item.payee_name}</span>
                  </div>
                ))}
                {dayData.items.length > 2 && (
                  <div className="text-gray-500">他{dayData.items.length - 2}件</div>
                )}
              </div>
              {dayData.unpaid_total > 0 && (
                <div className="mt-1 text-xs font-semibold text-red-600">
                  ¥{dayData.unpaid_total.toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Calendar className="w-6 h-6" />
          支払予定カレンダー
        </h1>
      </div>

      {/* アラートセクション */}
      {showAlerts && (alerts.overdue.length > 0 || alerts.urgent.length > 0) && (
        <div className="mb-6 space-y-3">
          {alerts.overdue.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-800">期限切れ</h3>
                <span className="text-sm text-red-600">({alerts.overdue.length}件)</span>
              </div>
              <div className="space-y-2">
                {alerts.overdue.slice(0, 3).map((alert, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <div>
                      <span className="font-medium">{alert.payee}</span>
                      <span className="text-gray-600 ml-2">({alert.reference})</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">¥{alert.amount.toLocaleString()}</div>
                      <div className="text-xs text-red-600">{alert.days_overdue}日経過</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {alerts.urgent.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-5 h-5 text-yellow-600" />
                <h3 className="font-semibold text-yellow-800">緊急（3日以内）</h3>
                <span className="text-sm text-yellow-600">({alerts.urgent.length}件)</span>
              </div>
              <div className="space-y-2">
                {alerts.urgent.slice(0, 3).map((alert, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <div>
                      <span className="font-medium">{alert.payee}</span>
                      <span className="text-gray-600 ml-2">({alert.reference})</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">¥{alert.amount.toLocaleString()}</div>
                      <div className="text-xs text-yellow-600">残り{Math.ceil(alert.days_remaining || 0)}日</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* カレンダー */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-4">
            {/* カレンダーヘッダー */}
            <div className="flex justify-between items-center mb-4">
              <button
                onClick={() => {
                  if (selectedMonth === 0) {
                    setSelectedMonth(11);
                    setSelectedYear(selectedYear - 1);
                  } else {
                    setSelectedMonth(selectedMonth - 1);
                  }
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <h2 className="text-lg font-semibold">
                {selectedYear}年 {selectedMonth + 1}月
              </h2>

              <button
                onClick={() => {
                  if (selectedMonth === 11) {
                    setSelectedMonth(0);
                    setSelectedYear(selectedYear + 1);
                  } else {
                    setSelectedMonth(selectedMonth + 1);
                  }
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* 曜日ヘッダー */}
            <div className="grid grid-cols-7 gap-0 mb-2">
              {['日', '月', '火', '水', '木', '金', '土'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* カレンダー本体 */}
            <div className="grid grid-cols-7 gap-0">
              {renderCalendar()}
            </div>
          </div>
        </div>

        {/* 選択日の詳細 */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-4">
            <h3 className="text-lg font-semibold mb-4">
              {selectedDate ? `${selectedDate} の支払予定` : '日付を選択してください'}
            </h3>

            {selectedDate && calendarData[selectedDate] ? (
              <div className="space-y-3">
                {calendarData[selectedDate].items.map((item, index) => (
                  <div key={index} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getPaymentTypeIcon(item.payment_type)}
                        <span className="text-sm font-medium">
                          {item.payment_type === 'purchase' ? '仕入' : '経費'}
                        </span>
                      </div>
                      {getStatusIcon(item.payment_status)}
                    </div>

                    <div className="text-sm space-y-1">
                      <div className="font-medium">{item.payee_name}</div>
                      <div className="text-gray-600">{item.reference_number}</div>
                      <div className="font-semibold text-lg">
                        ¥{item.amount.toLocaleString()}
                      </div>
                    </div>

                    {item.payment_status === 'unpaid' && (
                      <button
                        onClick={() => handlePaymentExecute(item)}
                        className="mt-3 w-full bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                      >
                        支払実行
                      </button>
                    )}
                  </div>
                ))}

                {calendarData[selectedDate].items.length > 0 && (
                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">合計</span>
                      <span className="text-lg font-bold">
                        ¥{calendarData[selectedDate].total.toLocaleString()}
                      </span>
                    </div>
                    {calendarData[selectedDate].unpaid_total > 0 && (
                      <div className="flex justify-between items-center text-sm text-red-600 mt-1">
                        <span>未払い</span>
                        <span className="font-semibold">
                          ¥{calendarData[selectedDate].unpaid_total.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : selectedDate ? (
              <p className="text-gray-500 text-center py-8">
                この日の支払予定はありません
              </p>
            ) : (
              <p className="text-gray-500 text-center py-8">
                カレンダーから日付を選択してください
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentScheduleCalendar;